import { useState, useEffect, useCallback } from 'react';
import { paymentReconApi } from '../services/paymentReconApi';
import type { ReconStats, ReconCategory, ReconBankAccount, ReconTemplate } from '../types/paymentRecon';

/**
 * Loads the shared reference data + KPI stats for the Payment Reconciliation
 * page. Individual tabs fetch their own filtered lists; this hook holds what
 * everything needs (stats, categories, bank accounts, templates).
 */
export function usePaymentReconciliation(companyCode = 'dfr') {
    const [stats, setStats] = useState<ReconStats | null>(null);
    const [nonCustomerCategories, setNonCustomerCategories] = useState<ReconCategory[]>([]);
    const [residualReasons, setResidualReasons] = useState<ReconCategory[]>([]);
    const [bankAccounts, setBankAccounts] = useState<ReconBankAccount[]>([]);
    const [templates, setTemplates] = useState<ReconTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refreshStats = useCallback(async () => {
        try {
            const s = await paymentReconApi.getStats(companyCode);
            setStats(s);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load stats');
        }
    }, [companyCode]);

    const refreshReference = useCallback(async () => {
        const [cats, banks, tpls] = await Promise.all([
            paymentReconApi.listCategories(),
            paymentReconApi.listBankAccounts(companyCode),
            paymentReconApi.listTemplates(companyCode),
        ]);
        setNonCustomerCategories(cats.nonCustomer);
        setResidualReasons(cats.residual);
        setBankAccounts(banks.bankAccounts);
        setTemplates(tpls.templates);
    }, [companyCode]);

    const reloadAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await Promise.all([refreshStats(), refreshReference()]);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [refreshStats, refreshReference]);

    useEffect(() => { void reloadAll(); }, [reloadAll]);

    return {
        stats, nonCustomerCategories, residualReasons, bankAccounts, templates,
        loading, error,
        refreshStats, refreshReference, reloadAll,
        setBankAccounts, setTemplates,
    };
}
